/**
 * Location Store - GPS coordinates & tracking history
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Coordinates, LocationHistory } from '@/types';

interface LocationState {
  // Current position data
  currentPosition: Coordinates | null;
  heading: number | null;
  accuracy: number | null;
  speed: number | null;
  timestamp: number | null;

  // Historical data for 500m cache
  history: LocationHistory[];

  // Tracking state
  isTracking: boolean;
  error: GeolocationPositionError | null;
  permissionStatus: 'granted' | 'denied' | 'prompt' | 'unknown';

  // Actions
  setPosition: (
    position: Coordinates,
    accuracy: number,
    heading?: number,
    speed?: number
  ) => void;
  startTracking: () => void;
  stopTracking: () => void;
  clearHistory: () => void;
  setError: (error: GeolocationPositionError | null) => void;
  setPermissionStatus: (status: 'granted' | 'denied' | 'prompt' | 'unknown') => void;
}

const MAX_HISTORY_POINTS = 100; // Keep last ~100 points for 500m coverage

export const useLocationStore = create<LocationState>()(
  persist(
    (set, get) => ({
      currentPosition: null,
      heading: null,
      accuracy: null,
      speed: null,
      timestamp: null,
      history: [],
      isTracking: false,
      error: null,
      permissionStatus: 'unknown',

      setPosition: (position, accuracy, heading, speed) => {
        const timestamp = Date.now();
        const { history } = get();

        // Add to history, keeping only recent points
        const newHistory = [
          ...history.slice(-(MAX_HISTORY_POINTS - 1)),
          { ...position, timestamp, accuracy },
        ];

        set({
          currentPosition: position,
          accuracy,
          heading: heading ?? null,
          speed: speed ?? null,
          timestamp,
          history: newHistory,
          error: null,
        });
      },

      startTracking: () => set({ isTracking: true, error: null }),

      stopTracking: () => set({ isTracking: false }),

      clearHistory: () => set({ history: [] }),

      setError: (error) =>
        set({
          error,
          isTracking: error ? false : get().isTracking,
        }),

      setPermissionStatus: (permissionStatus) => set({ permissionStatus }),
    }),
    {
      name: 'blurp-location-store',
      // Only persist history to localStorage
      partialize: (state) => ({
        history: state.history.slice(-50), // Only persist last 50 points
      }),
    }
  )
);
